/**
 * Money is a decimal string end to end in this codebase, so these operate on the
 * string and never parse it. A `Number` round-trip silently rounds at 16
 * significant digits and re-emits anything from 1e21 up in exponent form, which
 * the backend's decimal parser rejects.
 */

/** What a user may leave in the field: digits, at most one point, no sign. */
export const sanitizeMoneyValue = (input: string): string => {
  const cleaned = input.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');
  if (cleaned === '') return '';

  const [integer, fraction] = cleaned.split('.');
  const withoutLeadingZeros = integer.replace(/^0+(?=\d)/, '');

  return fraction === undefined ? withoutLeadingZeros : `${withoutLeadingZeros || '0'}.${fraction}`;
};

/** "250000" → "250,000". Digits only, so arbitrarily long values survive intact. */
export const groupMoneyValue = (value: string): string => {
  if (value === '') return '';

  const [integer, fraction] = value.split('.');
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return fraction === undefined ? grouped : `${grouped}.${fraction}`;
};

const fractionOf = (value: string) => value.split('.')[1] ?? '';

const toScaledInteger = (value: string, scale: number): bigint => {
  const [integer, fraction = ''] = value.split('.');
  return BigInt((integer || '0') + fraction.padEnd(scale, '0'));
};

/**
 * `a - b` on two decimal strings, via BigInt so no digit is lost and a
 * difference smaller than one unit does not round away. Returns the sign and
 * the unsigned remainder separately — callers place the sign outside the
 * currency symbol.
 */
export const subtractMoneyValues = (
  a: string,
  b: string,
): { negative: boolean; magnitude: string } => {
  const scale = Math.max(fractionOf(a).length, fractionOf(b).length);
  const difference = toScaledInteger(a, scale) - toScaledInteger(b, scale);
  const negative = difference < 0n;
  const digits = (negative ? -difference : difference).toString().padStart(scale + 1, '0');

  return {
    negative,
    magnitude: scale === 0 ? digits : `${digits.slice(0, -scale)}.${digits.slice(-scale)}`,
  };
};

/** What a completed amount looks like: digits, optionally a fraction, no sign. */
const isDecimalValue = (value: string) => /^\d+(\.\d+)?$/.test(value);

/** "12." is what a half-typed fraction looks like; read it as the "12" typed so far. */
const withoutTrailingPoint = (value: string) => (value.endsWith('.') ? value.slice(0, -1) : value);

/**
 * "12.345" → "12.34" at two decimals. Truncates rather than rounds: a field that
 * rewrites the digit before the one being typed reads as a bug, and the digit
 * dropped here is one the user is still in the middle of entering.
 */
export const limitMoneyDecimals = (value: string, decimals: number): string => {
  const [integer, fraction] = value.split('.');
  if (fraction === undefined || fraction.length <= decimals) return value;

  return decimals === 0 ? integer : `${integer}.${fraction.slice(0, decimals)}`;
};

/**
 * `a / b` on two decimal strings, to exactly `decimals` places, rounded half-up.
 * BigInt throughout for the same reason as `subtractMoneyValues`: these amounts
 * are held as strings precisely because a double cannot carry them.
 *
 * Returns null when there is no quotient to state — an unusable amount on either
 * side, or a divisor of zero — so callers can blank the derived field rather
 * than show a number that means nothing.
 */
export const divideMoneyValues = (
  dividend: string,
  divisor: string,
  decimals: number,
): string | null => {
  const a = withoutTrailingPoint(dividend);
  const b = withoutTrailingPoint(divisor);
  if (!isDecimalValue(a) || !isDecimalValue(b)) return null;

  const scale = Math.max(fractionOf(a).length, fractionOf(b).length);
  const divisorScaled = toScaledInteger(b, scale);
  if (divisorScaled === 0n) return null;

  const guarded = (toScaledInteger(a, scale) * 10n ** BigInt(decimals + 1)) / divisorScaled;
  const digits = ((guarded + 5n) / 10n).toString().padStart(decimals + 1, '0');

  return decimals === 0 ? digits : `${digits.slice(0, -decimals)}.${digits.slice(-decimals)}`;
};

export const isPositiveMoneyValue = (value: string): boolean =>
  isDecimalValue(value) && /[1-9]/.test(value);
